import { CommonModule } from '@angular/common';
import {
  Component,
  HostBinding,
  inject,
  OnDestroy,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatChipsModule } from '@angular/material/chips';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { AbbSimpleCardComponent } from '@components/abb-card/abb-simple-card.component';
import { AbbTimeInputComponent } from '@components/abb-form/abb-time-picker';
import {
  benchmarkSelectByQuery,
  benchmarkCategoryListQuery,
  benchmarkMetricListQuery,
  benchmarkDataChartQuery,
  benchmarkResolutionListQuery,
  benchmarkExportQuery,
} from '@graphql/property-services';
import {
  BenchmarkSelectByQuery,
  BenchmarkCategoryListQuery,
  BenchmarkMetricListQuery,
  BenchmarkDataChartQuery,
  BenchmarkResolutionListQuery,
  UtcQuery,
  DeviceListQuery,
  DeviceListQueryVariables,
  Status,
  AddToReportHistoryMutation,
  AddToReportHistoryMutationVariables,
  BenchmarkExportQuery,
  BenchmarkExportQueryVariables,
  Report_Category,
} from 'gql/graphql';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import { Apollo } from 'apollo-angular';
import { AbbCommonModule } from 'app/abb-common.module';
import { first, Subscription } from 'rxjs';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import {
  combineDateTime,
  combineDateTimeLocale,
  DATE_LOCALE_GB_FORMAT,
  getFormattedXValuesDate,
  parseToLocaleDateString,
} from '@utils/datetime';
import { AbbChartComponent } from '@components/abb-chart/abb-chart.component';
import { CacheLoggerService } from '@services/logs/cacheLogger.service';
import jsPDF from 'jspdf';
import { TranslateService } from '@ngx-translate/core';
import { PALETTE } from '@constants/colors';
import { getColor, parseRgb } from '@utils/color';
import { ToastService } from '@services/ui/toast.service';
import { FilterOption } from '@components/abb-table/abb-table.component';
import { getResolutionSelectOptions } from '@pages/property-services/property-services';
import { UserSettingsService } from '@services/user/user-settings.service';
import { getErrorMessage } from '@utils/errors';
import { t } from '@utils/strings';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { downloadBase64File } from '@utils/download';
import { addToReportHistoryMutation, reportListQuery } from '@graphql/report';

@Component({
  selector: 'abb-benchmarks',
  standalone: true,
  host: {
    class: 'pr-4',
  },
  imports: [
    CommonModule,
    AbbSimpleCardComponent,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatChipsModule,
    MatDatepickerModule,
    AbbCommonModule,
    AbbTimeInputComponent,
    TPipe,
    AbbChartComponent,
    MatProgressSpinner,
  ],
  template: `
    <abb-simple-card class="min-h-[98.7%] pb-2" [radiusSize]="'small'">
      <div content class="flex-1 flex gap-2">
        <form
          [formGroup]="form"
          class="flex flex-col flex-wrap gap-x-3 gap-y-2"
        >
          <div class="w-[400px]">
            <mat-form-field class="w-full" color="primary">
              <mat-label>{{ 'Benchmark' | t }}</mat-label>
              <mat-select
                formControlName="benchmark"
                id="benchmark"
                name="benchmark"
                [panelClass]="'panel select-panel'"
              >
                @for (group of benchmarkSelectBy(); track group.subOptions ?
                group: group.option['value']) { @if(group.subOptions){
                <mat-optgroup
                  [label]="group.option.label | t"
                  class="cursor-default"
                >
                  @for (asset of group.subOptions; track asset['value']) {
                  <mat-option
                    [id]="group.option['value'] + '_' + asset['value']"
                    [value]="group.option['value'] + '_' + asset['value']"
                    >{{ asset['label'] | t }}</mat-option
                  >
                  }
                </mat-optgroup>
                } @else {
                <mat-option
                  [id]="group.option['value'].toString()"
                  [value]="group.option['value']"
                  >{{ group.option['label'] | t }}</mat-option
                >
                } }
              </mat-select>
            </mat-form-field>
            @if(!form.controls.benchmark.value) {
            <span class="text-xs font-medium text-abb-font/60">{{
              'Select a benchmark to get started' | t
            }}</span>
            }
          </div>
          @if(form.controls.benchmark.value){
          <div class="w-[400px]">
            <mat-form-field class="w-full" color="primary">
              <mat-label>{{ 'Category' | t }}</mat-label>
              <mat-select
                formControlName="benchmarkCategory"
                id="benchmarkCategory"
                name="benchmarkCategory"
                multiple
                [panelClass]="'panel border-font-checkbox'"
              >
                <mat-select-trigger>
                  <mat-chip-listbox>
                    @for (assetId of form.controls.benchmarkCategory.value;
                    track assetId) {
                    <mat-chip
                      [id]="assetId.toString()"
                      [removable]="true"
                      (removed)="onAssetRemoved(assetId)"
                      class="chips"
                    >
                      {{ assetWith(assetId) }}
                      <abb-icon
                        id="remove"
                        matChipRemove
                        [name]="'mat/cancel'"
                        size="small"
                      ></abb-icon>
                    </mat-chip>
                    }
                  </mat-chip-listbox>
                </mat-select-trigger>
                @for (asset of benchmarkCategory(); track asset['value']) {
                <mat-option
                  [id]="asset['value'].toString()"
                  [value]="asset['value']"
                  >{{ asset['label'] | t }}</mat-option
                >
                }
              </mat-select>
            </mat-form-field>
          </div>
          @if(form.controls.benchmarkCategory.value) {
          <div class="w-[400px]">
            <mat-form-field class="w-full" color="primary">
              <mat-label>{{ 'Metric' | t }}</mat-label>
              <mat-select
                id="metric"
                formControlName="metric"
                name="metric"
                [panelClass]="'panel'"
                (selectionChange)="onYAxisSelectionChange($event)"
              >
                <mat-option [value]="null">-</mat-option>
                @for (metric of benchmarkMetric(); track metric['value']) {
                <mat-option
                  [id]="metric['value'].toString()"
                  [value]="metric['value']"
                  >{{ metric['label'] | t }}</mat-option
                >
                }
              </mat-select>
            </mat-form-field>
          </div>
          @if(form.controls.metric.value) {
          <div
            formGroupName="interval"
            class="flex flex-row w-[400px] flex-wrap gap-3 h-fit"
          >
            <mat-form-field color="primary" class="w-full">
              <mat-label>{{ 'From' | t }}</mat-label>
              <input
                matInput
                id="fromDate"
                [matDatepicker]="picker1"
                (ngModelChange)="
                  dateChange($event, form.controls.interval.controls.fromTime)
                "
                [placeholder]="'From' | t"
                formControlName="fromDate"
                class="placeholder:text-abb-font/40"
              />
              <span class="w-[100px] text-left inline-block" matTextSuffix>
                @if(!picker1.opened) {
                <input
                  formControlName="fromTime"
                  type="time"
                  name="fromTime"
                  id="fromTime"
                  class="bg-inherit focus:outline-none text-abb-font/85"
                  step="1"
                  [ngClass]="
                    form.controls.interval.controls.fromDate.value
                      ? 'cursor-text'
                      : 'opacity-0 focus:opacity-100 pointer-events-none'
                  "
                  (click)="
                    form.controls.interval.controls.fromDate.value
                      ? $event.stopPropagation()
                      : null
                  "
                />
                }@else if(form.controls.interval.controls.fromDate.value) {
                {{ form.controls.interval.controls.fromTime.value }}
                }
              </span>
              <mat-datepicker-toggle
                matIconSuffix
                [for]="picker1"
              ></mat-datepicker-toggle>
              <mat-datepicker #picker1 [panelClass]="'panel'">
                <mat-datepicker-actions>
                  <div class="!flex !flex-col !w-full">
                    @if(picker1.opened) {
                    <abb-time-input
                      class="!w-full !flex !justify-center"
                      id="fromTime"
                      formControlName="fromTime"
                    ></abb-time-input
                    >}
                    <div class="flex flex-row w-full gap-5 p-5 justify-end">
                      <abb-button
                        id="clean-btn"
                        [variant]="'clean'"
                        matDateRangePickerCancel
                        >{{ 'cancel' | t }}</abb-button
                      >
                      <abb-button id="apply-btn" matDateRangePickerApply>{{
                        'Apply' | t
                      }}</abb-button>
                    </div>
                  </div>
                </mat-datepicker-actions>
              </mat-datepicker>
            </mat-form-field>

            <mat-form-field color="primary" class="w-full">
              <mat-label>{{ 'To' | t }}</mat-label>
              <input
                matInput
                id="toDate"
                [min]="form.controls.interval.controls.fromDate.value"
                [matDatepicker]="picker2"
                (ngModelChange)="
                  dateChange($event, form.controls.interval.controls.toTime)
                "
                [placeholder]="'To' | t"
                formControlName="toDate"
                class="placeholder:text-abb-font/40"
              />
              <span class="w-[100px] text-left inline-block" matTextSuffix>
                @if(!picker2.opened) {
                <input
                  formControlName="toTime"
                  type="time"
                  id="toTime"
                  name="toTime"
                  class="bg-inherit focus:outline-none text-abb-font/85"
                  step="1"
                  [ngClass]="
                    form.controls.interval.controls.toDate.value
                      ? 'cursor-text'
                      : 'opacity-0 focus:opacity-100 pointer-events-none'
                  "
                  (click)="
                    form.controls.interval.controls.toDate.value
                      ? $event.stopPropagation()
                      : null
                  "
                />
                }@else if(form.controls.interval.controls.toDate.value) {
                {{ form.controls.interval.controls.toTime.value }} }
              </span>
              <mat-datepicker-toggle
                matSuffix
                [for]="picker2"
              ></mat-datepicker-toggle>
              <mat-datepicker #picker2 [panelClass]="'panel'">
                <mat-datepicker-actions>
                  <div class="!flex !flex-col !w-full">
                    @if(picker2.opened) {
                    <abb-time-input
                      class="!w-full !flex !justify-center"
                      id="toTime"
                      formControlName="toTime"
                    ></abb-time-input
                    >}
                    <div class="flex flex-row w-full gap-5 p-5 justify-end">
                      <abb-button
                        id="clean-btn"
                        [variant]="'clean'"
                        matDateRangePickerCancel
                        >{{ 'cancel' | t }}</abb-button
                      >
                      <abb-button id="apply-btn" matDateRangePickerApply>{{
                        'Apply' | t
                      }}</abb-button>
                    </div>
                  </div>
                </mat-datepicker-actions>
              </mat-datepicker>
            </mat-form-field>
          </div>
          @if(form.controls.interval.value.fromDate &&
          form.controls.interval.value.toDate) {
          <div class="w-[400px]">
            <mat-form-field class="w-full" color="primary">
              <mat-label>{{ 'Resolution' | t }}</mat-label>
              <mat-select
                formControlName="resolution"
                id="resolution"
                name="resolution"
                [panelClass]="'panel'"
                (selectionChange)="onXAxisSelectionChange($event)"
              >
                <mat-option [value]="null">-</mat-option>
                @for (resolution of filteredBenchmarkResolution(); track
                resolution['value']) {
                <mat-option
                  [id]="resolution['value'].toString()"
                  [value]="resolution['value']"
                  >{{ resolution['label'] }}</mat-option
                >
                }
              </mat-select>
            </mat-form-field>
          </div>
          }} }}
        </form>
        @if(loading()) {
        <div class="flex flex-col justify-center items-center w-full">
          <mat-progress-spinner
            [diameter]="60"
            color="primary"
            mode="indeterminate"
          ></mat-progress-spinner>
        </div>
        } @else if (benchmarkDataChart()) {
        <div class="flex flex-col w-full">
          <div class="flex justify-end mt-4">
            <abb-button id="export" (onclick)="onExportCSV()" class="w-32">{{
              'Export' | t
            }}</abb-button>
          </div>
          <div class="h-full w-full block">
            <abb-chart
              id="benchmarkDataChart"
              class="block"
              [dataChart]="benchmarkDataChart()!"
              [yAxisLabel] = "yAxis()"
              [xAxisLabel] = "xAxis()"
              [chartType]="

                benchmarkDataChart()!.yValues.length > 15 ? 'line' : 'bar'
              "
              [showColumnChartLabels]="true"
              [grow]="true"
            ></abb-chart>
          </div>
        </div>
        }
      </div>
    </abb-simple-card>
  `,
  encapsulation: ViewEncapsulation.None,
  styles: `
    .select-panel {
      .mat-mdc-optgroup {
        --mdc-list-list-item-label-text-color: var(--abb-color-font) !important;
      }
      .mat-mdc-optgroup-label {
        opacity: 0.6 !important;
      }
    }
  `,
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    { provide: MAT_DATE_FORMATS, useValue: DATE_LOCALE_GB_FORMAT },
    {
      provide: TRANSLATION_PREFIX,
      useValue: 'property_services.tabs.benchmarks',
    },
  ],
})
export class BenchmarksComponent implements OnDestroy {
  @HostBinding('attr.id') componentid = 'benchmarks-component';
  benchmarkSelectBy = signal<Array<{
    option: FilterOption;
    subOptions: FilterOption[] | undefined;
  }> | null>(null);
  benchmarkCategory = signal<FilterOption[] | undefined>([]);
  benchmarkMetric = signal<FilterOption[]>([]);
  benchmarkResolution = signal<FilterOption[]>([]);
  filteredBenchmarkResolution = signal<FilterOption[]>([]);
  form = new FormGroup({
    benchmark: new FormControl<string | null>(null, [Validators.required]),
    benchmarkCategory: new FormControl<Array<string | number> | null>(null, [
      Validators.required,
    ]),
    metric: new FormControl<string | null>(null, [Validators.required]),
    interval: new FormGroup({
      fromDate: new FormControl<Date | null>(null, [Validators.required]),
      fromTime: new FormControl<string | null>(null, [Validators.required]),
      toDate: new FormControl<Date | null>(null, [Validators.required]),
      toTime: new FormControl<string | null>(null, [Validators.required]),
    }),
    resolution: new FormControl<string | null>(null, [Validators.required]),
  });
  benchmarkDataChart = signal<
    BenchmarkDataChartQuery['benchmarkDataChart'] | null
  >(null);
  yAxis = signal(
   { text : '',
    unit : ''
   }

  )
  xAxis =signal(
   { text : '',
    unit : ''
   }

  )
  locale = signal(this.translate.currentLang);
  loading = signal<boolean>(false);
  exporting = signal(false);

onXAxisSelectionChange(event: MatSelectChange): void {
  const selectedValue = event.value;

  const selectedResolution = this.benchmarkResolution().find(
    resolution => resolution.value === selectedValue
  );

  if (selectedResolution) {
    this.xAxis.set({
      text: selectedResolution.label,
      unit: '' // se hai unità, puoi aggiungerla
    });
  } else {
    this.xAxis.set({ text: '', unit: '' });
  }
}

onYAxisSelectionChange(event: MatSelectChange): void {
  const selectedValue = event.value;

  const selectedMetric = this.benchmarkMetric().find(
    metric => metric.value === selectedValue
  );

  if (selectedMetric) {
    this.yAxis.set({
      text: selectedMetric.label,
      unit: '' // se hai unità, puoi aggiungerla
    });
  } else {
    this.yAxis.set({ text: '', unit: '' });
  }
}




  private readonly prefix = inject(TRANSLATION_PREFIX);
  private readonly subscriptions: Subscription[] = [];
  constructor(
    private readonly apollo: Apollo,
    private readonly translate: TranslateService,
    private readonly toast: ToastService,
    readonly cacheLogger: CacheLoggerService,
    readonly userService: UserSettingsService
  ) {
    // this.timezone.set(userService.timezone());
    this.subscriptions.push(
      //TODO be: aggiungere opzione 'only'
      apollo
        .watchQuery<BenchmarkSelectByQuery>({
          query: benchmarkSelectByQuery,
        })
        .valueChanges.subscribe({
          next: ({ data }) => {
            this.benchmarkSelectBy.set(
              data.benchmarkSelectBy.map((b) => {
                return {
                  option: {
                    value: b.id,
                    label: b.name,
                  },
                  subOptions: b.categories
                    ? b.categories.map((c) => ({
                        value: c!.id,
                        label: c!.name,
                      }))
                    : undefined,
                };
              })
            );
          },
          error: (err) => {
            //let errMsg = getErrorMessage(err, this.translate);
            //this.toast.notifyError(errMsg);
          },
        }),
      apollo
        .watchQuery<BenchmarkResolutionListQuery>({
          query: benchmarkResolutionListQuery,
        })
        .valueChanges.subscribe({
          next: ({ data }) => {
            const benchmarkResolutionList: Array<{
              value: string;
              label: string;
            }> = data.benchmarkResolutionList?.map((r) => {
              return {
                value: r?.id,
                label: r?.name,
                skipTranslation: true,
              };
            });
            this.benchmarkResolution.set(benchmarkResolutionList);
          },
          error: (err) => {
            //let errMsg = getErrorMessage(err, this.translate);
            //this.toast.notifyError(errMsg);
          },
        }),
      this.form.controls.benchmark.valueChanges.subscribe((benchmark) => {
        this.resetDynamicFormAndChart();
        if (benchmark) {
          const composedId = benchmark.split('_');
          this.subscriptions.push(
            apollo
              .query<BenchmarkCategoryListQuery>({
                query: benchmarkCategoryListQuery,
                variables: {
                  benchmarkId: composedId[0],
                  categoryId: composedId[1] ?? undefined,
                },
              })
              .pipe(first())
              .subscribe({
                next: ({ data }) => {
                  const benchmarkCategoryList:
                    | Array<{
                        value: string;
                        label: string;
                      }>
                    | undefined = data.benchmarkCategoryList?.map((a) => {
                    return {
                      value: a?.id,
                      label: a?.name,
                    };
                  });
                  this.benchmarkCategory.set(benchmarkCategoryList);
                },
                error: (err) => {
                  //let errMsg = getErrorMessage(err, this.translate);
                  //this.toast.notifyError(errMsg);
                },
              })
          );
        }
      }),
      this.form.controls.benchmarkCategory.valueChanges.subscribe(
        (category) => {
          const benchmark = this.form.controls.benchmark.value;
          if (benchmark && category && category.length > 0) {
            const composedId = benchmark.split('_');
            this.subscriptions.push(
              apollo
                .query<BenchmarkMetricListQuery>({
                  query: benchmarkMetricListQuery,
                  variables: {
                    benchmarkId: composedId[0],
                    categoryId: composedId[1] ?? undefined,
                  },
                })
                .pipe(first())
                .subscribe({
                  next: ({ data }) => {
                    const benchmarkMetricList: Array<{
                      value: string;
                      label: string;
                    }> = data.benchmarkMetricList?.map((m) => {
                      return {
                        value: m?.id,
                        label: m?.name,
                      };
                    });
                    this.benchmarkMetric.set(benchmarkMetricList);
                  },
                  error: (err) => {
                    //let errMsg = getErrorMessage(err, this.translate);
                    //this.toast.notifyError(errMsg);
                  },
                })
            );
          } else if (category?.length == 0) {
            this.resetDynamicFormAndChart();
          }
        }
      ),
      this.form.controls.interval.valueChanges.subscribe((interval) => {
        if (interval.fromDate && interval.toDate) {
          this.form.controls.resolution.reset();
          let resolutionSelectOptions = getResolutionSelectOptions(
            this.benchmarkResolution(),
            combineDateTimeLocale(interval.fromDate, interval.fromTime ?? ''),
            combineDateTimeLocale(interval.toDate, interval.toTime ?? '')
          );
          this.filteredBenchmarkResolution.set(resolutionSelectOptions);
        }
      }),
      this.form.valueChanges.subscribe((v) => {
        if (!this.form.valid) {
          this.benchmarkDataChart.set(null);
          return;
        }
        const filters = v;
        const { benchmark, benchmarkCategory, metric, interval, resolution } =
          filters;
        if (benchmark) {
          const composedId = benchmark.split('_');
          const benchmarkId = composedId[0];
          const categoryId = composedId[1] ?? undefined;
          this.loading.set(true);

          this.fetchChartData({
            benchmarkId,
            categoryId,
            benchmarkCategoryIds: benchmarkCategory,
            metricId: metric,
            interval,
            resolutionId: resolution,
          });
        }
      })
    );
  }

  customLabels = signal<boolean>(true);
  fetchChartData = ({
    benchmarkId,
    categoryId,
    benchmarkCategoryIds,
    metricId,
    interval,
    resolutionId,
  }: Partial<{
    benchmarkId: string | null;
    categoryId: string | null;
    benchmarkCategoryIds: Array<string | number> | null;
    metricId: string | null;
    interval: Partial<{
      fromDate: Date | null;
      fromTime: string | null;
      toDate: Date | null;
      toTime: string | null;
    }>;
    resolutionId: string | null;
  }>) => {
    this.subscriptions.push(
      this.apollo
        .query<BenchmarkDataChartQuery>({
          query: benchmarkDataChartQuery,
          variables: {
            benchmarkId,
            categoryId,
            benchmarkCategoryIds,
            metricId,
            fromTs: interval?.fromDate
              ? combineDateTimeLocale(
                  interval?.fromDate,
                  interval?.fromTime ?? ''
                )
              : null,
            toTs: interval?.toDate
              ? combineDateTimeLocale(interval?.toDate, interval?.toTime ?? '')
              : null,
            resolutionId,
          },
        })
        .pipe(first())
        .subscribe({
          next: ({ data }) => {
            let dataChart = { ...data.benchmarkDataChart };

            dataChart.xValues = dataChart.xValues.map((xVal) => {
              const timestamp = Date.parse(xVal);
              if (isNaN(timestamp) || this.customLabels()) {
                return xVal;
              }
              const date = new Date(timestamp);
              return date.toLocaleDateString(navigator.language, {
                day: '2-digit',
                month: '2-digit',
              });
            });
            if (this.customLabels() && resolutionId) {
              dataChart.xValues = getFormattedXValuesDate(
                dataChart.xValues,
                resolutionId,
                this.translate.currentLang,
                interval?.fromDate
                  ? combineDateTimeLocale(
                      interval?.fromDate ?? '',
                      interval?.fromTime ?? ''
                    )
                  : null,
                interval?.toDate
                  ? combineDateTimeLocale(
                      interval?.toDate ?? '',
                      interval?.toTime ?? ''
                    )
                  : null
              );
            }

            this.benchmarkDataChart.set(dataChart);

            console.log(this.benchmarkDataChart(), 'data chart');

            this.loading.set(false);
          },
          error: (err) => {
            //let errMsg = getErrorMessage(err, this.translate);
            //this.toast.notifyError(errMsg);
          },
        })
    );
  };

  resetDynamicFormAndChart() {
    this.form.patchValue(
      {
        benchmarkCategory: null,
        interval: {
          fromDate: null,
          fromTime: null,
          toDate: null,
          toTime: null,
        },
        metric: null,
        resolution: null,
      },
      { emitEvent: false }
    );
    this.benchmarkDataChart.set(null);
  }

  assetWith(assetId: string | number) {
    return this.benchmarkCategory()?.find((p) => p['value'] === assetId)![
      'label'
    ];
  }

  onAssetRemoved(option: string | number) {
    const assets = this.form.controls.benchmarkCategory.value as Array<
      string | number
    >;
    this.removeFirst(assets, option);
    this.form.controls.benchmarkCategory.setValue(assets);
  }

  private removeFirst<T>(array: T[], toRemove: T): void {
    const index = array.indexOf(toRemove);
    if (index !== -1) {
      array.splice(index, 1);
    }
  }

  dateChange(e: Date, ctrl: FormControl) {
    if (!e) {
      ctrl.reset();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  onExportCSV() {
    this.exporting.set(true);
    const formValues = this.form.value;
    const composedId = formValues.benchmark?.split('_');
    const benchmarkId = composedId?.[0] ?? '';
    const categoryId = composedId?.[1] ?? '';

    if (
      formValues.interval?.fromDate &&
      formValues.interval?.fromTime &&
      formValues.interval?.toDate &&
      formValues.interval?.toTime
    ) {
      const fromTs = combineDateTimeLocale(
        formValues.interval.fromDate,
        formValues.interval.fromTime
      );

      const toTs = combineDateTimeLocale(
        formValues.interval.toDate,
        formValues.interval.toTime
      );

      this.apollo
        .query<BenchmarkExportQuery, BenchmarkExportQueryVariables>({
          query: benchmarkExportQuery,
          variables: {
            benchmark: benchmarkId,
            categoryId: categoryId,
            benchmarkCategory: formValues.benchmarkCategory?.map(String) ?? [],
            metric: formValues.metric ?? '',
            fromTs: fromTs,
            toTs: toTs,
            resolution: formValues.resolution ?? '',
          },
        })
        .subscribe({
          next: ({ data }) => {
            downloadBase64File(data.benchmarkExport);
            this.apollo
              .mutate<
                AddToReportHistoryMutation,
                AddToReportHistoryMutationVariables
              >({
                mutation: addToReportHistoryMutation,
                variables: {
                  id: data.benchmarkExport.id,
                  filename: data.benchmarkExport.filename,
                  type: Report_Category.Historical,
                  content: data.benchmarkExport.content,
                  size: data.benchmarkExport.size,
                },
                update: (cache, { data }) => {
                  cache.writeQuery({
                    query: reportListQuery,
                    data: { reportList: data?.addToReportHistory },
                  });
                },
              })
              .pipe(first())
              .subscribe();
            this.exporting.set(false);
            this.toast.notifySuccess(
              t(this.prefix, `File exported successfully`, this.translate)
            );
          },
          error: () => {
            this.exporting.set(false);
            this.toast.notifyError(
              t(
                this.prefix,
                `An error occurred while exporting file`,
                this.translate
              )
            );
          },
        });
    }
  }
}
